import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  signal,
} from '@angular/core';
import { CoinflipCoinComponent } from '../../coinflip-coin/coinflip-coin.component';
import { CoinflipCoin } from '@features/games/coinflip/types/coins.type';
import { Skin } from '@core/interfaces/skin.interface';
import { ModalService } from '@core/modals/services/modal.service';
import { CoinflipDeprecatedService } from '@features/games/coinflip/coinflip.service';
import {
  InventoryRequirementsOptions,
  InventoryTemplateComponent,
} from '@core/components/inventory-template/inventory-template.component';
import { Coinflip } from '@features/games/coinflip/interfaces/coinflip.interface';
import { ToastrService } from 'ngx-toastr';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgpTooltip, NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { OldSliderComponent } from '@core/components/old-slider/old-slider.component';
import { CostComponent } from '@core/components/cost/cost.component';
import {
  COINFLIP_MAX_ITEMS,
  COINFLIP_NON_KEY_BLOCK_AT_KEYS,
  COINFLIP_PURE_KEY_ONLY_MAX_ITEMS,
  isMannCoKey,
} from '@core/constants/coinflip-limits.constants';

/** Stable reference for `skinUnavailable`, so the input only toggles on/off. */
const isNonKey = (skin: Skin): boolean => !isMannCoKey(skin.name);

@Component({
  selector: 'app-coinflip-deposit-modal',
  imports: [
    InventoryTemplateComponent,
    CoinflipCoinComponent,
    CostComponent,
    SvgIconComponent,
    NgpTooltip,
    NgpTooltipTrigger,
    OldSliderComponent,
  ],
  templateUrl: './coinflip-deposit-modal.component.html',
  styleUrl: './coinflip-deposit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.create]': '!isJoinModal()',
  },
})
export class CoinflipDepositModalComponent {
  coinflip = input<Coinflip>();
  isJoinModal = computed(() => !!this.coinflip());
  public readonly minJoinDifference = 10;
  public readonly maxJoinDifference = 50;
  readonly coin = signal<CoinflipCoin>(1);
  readonly joinDifference = signal<number>(this.minJoinDifference);
  readonly pure = model<boolean>(false);
  readonly selectedSkins = model<Skin[]>([]);
  readonly isProcessing = signal(false);

  #composition = computed(() => {
    let keys = 0;
    let nonKeys = 0;
    for (const skin of this.selectedSkins()) {
      if (isMannCoKey(skin.name)) {
        keys++;
      } else {
        nonKeys++;
      }
    }
    return { keys, nonKeys };
  });

  /** Pure create whose selection holds no non-keys (keys may be 0). */
  #isKeyOnlyPureCreate = computed(
    () => this.pure() && !this.isJoinModal() && this.#composition().nonKeys === 0
  );

  #maxItems = computed(() =>
    this.#isKeyOnlyPureCreate() && this.#composition().keys > 0
      ? COINFLIP_PURE_KEY_ONLY_MAX_ITEMS
      : COINFLIP_MAX_ITEMS
  );

  requirementsOptions = computed<InventoryRequirementsOptions>(() => ({
    requirements_title: this.isJoinModal() ? 'Join Coinflip' : 'Create Coinflip',
    requirements_accent_word: 'bet',
    requirements_options_min_value: this.isJoinModal()
      ? this.coinflip()?.min_val ?? 0
      : 2,
    requirements_options_max_value: this.isJoinModal()
      ? this.coinflip()?.max_val ?? 0
      : 5000,
    requirements_options_min_items: 1,
    requirements_options_max_items: this.#maxItems(),
  }));

  /** Once 20+ keys are picked in key-only pure create, non-keys are blocked. */
  nonKeyBlocker = computed(() =>
    this.#isKeyOnlyPureCreate() &&
    this.#composition().keys >= COINFLIP_NON_KEY_BLOCK_AT_KEYS
      ? isNonKey
      : null
  );

  constructor(
    private coinflipService: CoinflipDeprecatedService,
    private modalService: ModalService,
    private toastService: ToastrService
  ) {
    effect(() => {
      if (this.isJoinModal()) {
        const coinflip = this.coinflip();
        if (coinflip) {
          this.coin.set(coinflip.host.side === 1 ? 2 : 1);
        }
      }
    });

    const isPureEffect = effect(() => {
      if (this.coinflip()?.is_pure) {
        this.pure.set(true);
        isPureEffect.destroy();
      }
    });
  }

  public async createCoinflip() {
    try {
      this.isProcessing.set(true);
      const trade = await this.coinflipService.createCoinflip({
        skins: this.selectedSkins(),
        side: this.coin(),
        joinDifference: Number(this.joinDifference().toFixed()),
        isPure: this.pure(),
      });

      this.modalService.openModal('TradeModal', {
        tradeModalOptions: {
          trade_offer_id: trade.offerid,
          trade_hash: trade.hash,
          trade_skins: this.selectedSkins(),
          typography_trade_for: 'create the coinflip',
        },
        balance: this.pure() ? 'pure' : 'skins',
      });
      this.modalService.closeModals(['CoinflipDepositModal']);
    } catch {
    } finally {
      this.isProcessing.set(false);
    }
  }

  public async joinCoinflip() {
    const coinflip = this.coinflip();

    if (!coinflip) {
      this.toastService.error('No coinflip found');
      return;
    }
    this.isProcessing.set(true);
    try {
      const trade = await this.coinflipService.joinCoinflip(
        coinflip.gameid,
        this.selectedSkins()
      );
      this.modalService.openModal('TradeModal', {
        tradeModalOptions: {
          trade_offer_id: trade.offerid,
          trade_hash: trade.hash,
          trade_skins: this.selectedSkins(),
          typography_trade_for: 'join the coinflip',
        },
        balance: this.pure() ? 'pure' : 'skins',
      });
      this.modalService.closeModals(['CoinflipDepositModal']);
    } catch {
    } finally {
      this.isProcessing.set(false);
    }
  }

  public setCoin(coin: CoinflipCoin) {
    if (this.isJoinModal()) {
      this.toastService.error('You cannot chose the side you are joining');
      return;
    }

    this.coin.set(coin);
  }
}
